export const TRANSPORT_STATUS_CREATED = 'created';
export const TRANSPORT_STATUS_SHIPPING_CREATED = 'shippingCreated';
export const TRANSPORT_STATUS_ON_THE_WAY = 'onTheWay';
export const TRANSPORT_STATUS_AT_PICKUP_POINT = 'atPickupPoint';
export const TRANSPORT_STATUS_DELIVERED = 'delivered';
export const TRANSPORT_STATUS_RETURNING_BACK = 'returningBack';
export const TRANSPORT_STATUS_RETURNED_BACK = 'returnedBack';
export const TRANSPORT_STATUS_CANCELED = 'canceled';
export const TRANSPORT_STATUS_INCORRECT = 'incorrect';
export const TRANSPORT_STATUS_LOST = 'lost';

export const TransportStatuses = [
    TRANSPORT_STATUS_CREATED,
    TRANSPORT_STATUS_SHIPPING_CREATED,
    TRANSPORT_STATUS_ON_THE_WAY,
    TRANSPORT_STATUS_AT_PICKUP_POINT,
    TRANSPORT_STATUS_DELIVERED,
    TRANSPORT_STATUS_RETURNING_BACK,
    TRANSPORT_STATUS_RETURNED_BACK,
    TRANSPORT_STATUS_CANCELED,
    TRANSPORT_STATUS_INCORRECT,
    TRANSPORT_STATUS_LOST
] as const;

export type TransportStatus = (typeof TransportStatuses)[number];

export const TRANSPORT_TYPE_EXPEDITION = 'expedition';
export const TRANSPORT_TYPE_RETURN = 'return';
export const TRANSPORT_TYPE_ORDER = 'order';

export const TransportTypes = [TRANSPORT_TYPE_EXPEDITION, TRANSPORT_TYPE_RETURN, TRANSPORT_TYPE_ORDER] as const;

export type TransportType = (typeof TransportTypes)[number];

/** Types that can be selected manually in MySkladon / POST /api/secure/transports */
export const TransportUserSelectableTypes = [TRANSPORT_TYPE_EXPEDITION] as const;

export type TransportUserSelectableType = (typeof TransportUserSelectableTypes)[number];

export const TRANSPORT_ORDER_ROLE_SYSTEM = 'system';
export const TRANSPORT_ORDER_ROLE_MANUAL = 'manual';

export const TransportOrderRoles = [TRANSPORT_ORDER_ROLE_SYSTEM, TRANSPORT_ORDER_ROLE_MANUAL] as const;

export type TransportOrderRole = (typeof TransportOrderRoles)[number];

export const TRANSPORT_ORDER_MODE_ORDER = 'order';
export const TRANSPORT_ORDER_MODE_VALIDATE = 'validate';

export const TransportOrderModes = [TRANSPORT_ORDER_MODE_ORDER, TRANSPORT_ORDER_MODE_VALIDATE] as const;

export type TransportOrderMode = (typeof TransportOrderModes)[number];

/** Shippers allowed for automatic/manual B2B shipments (parity with shipping-ms CreateShipping). */
export const B2B_ALLOWED_SHIPPERS = [
    'PPL',
    'UPS',
    'GLS',
    'DHL',
    'CP',
    'DPD',
    'PERSONAL_PICKUP',
    'zasilkovna',
    'GLSSK'
] as const;

export type B2BAllowedShipper = (typeof B2B_ALLOWED_SHIPPERS)[number];

/** Order statuses allowed for manual create-shipping (parity with shipping-ms processSingleOrder). */
export const SHIPMENT_MANUAL_ORDER_STATUSES = [
    'waitingForShipment',
    'waitingForCarrier',
    'incorrect',
    'inProcess',
    'packed',
    'received'
] as const;

export type ShipmentManualOrderStatus = (typeof SHIPMENT_MANUAL_ORDER_STATUSES)[number];

export const SHIPMENT_SERVICE_CODES = [
    'GLSCZ',
    'GLSCZCOD',
    'GLSEU',
    'GLSEUCOD',
    'TGCZ',
    'TGCZCOD',
    'TGEU',
    'TGEUCOD',
    'CPDR',
    'CPDRCOD',
    'CPDROW',
    'CPDROWCOD',
    'CPNB',
    'CPNBCOD',
    'RRCZ',
    'RRCZCOD',
    'RREU',
    'RRWR',
    'UPSES',
    'UPSESDDP',
    'UPSESENV',
    'UPSSD',
    'UPSSDDDP',
    'PCKT',
    'PCKTCOD',
    'PPLCZ',
    'PPLCZCOD',
    'PPLEU',
    'PPLEUCOD',
    'PPLBOX',
    'DHLESIDAP',
    'DHLESIDDP',
    'DHLWPXDAP',
    'DHLWPXDDP',
    'DHLECX',
    'DHLESU',
    'DHLD2SVP',
    'DPD',
    'DPDPS',
    'PERSONAL_PICKUP',
    'PPLPP',
    'GLSSK',
    'GLSSKPS',
    'GLSPS',
    'GLS24H',
    'GLSXS',
    'GLSSKXS',
    'DPDXS'
] as const;

export const CUSTOM_SHIPPERS = ['VLASTNI', 'VLASTNÍ', 'CUSTOM', 'CUSTOM PARCEL', 'CUSTOM PALLET'] as const;

export const TransportStatusesTo: Record<TransportStatus, readonly TransportStatus[]> = {
    [TRANSPORT_STATUS_CREATED]: [
        TRANSPORT_STATUS_SHIPPING_CREATED,
        TRANSPORT_STATUS_CANCELED,
        TRANSPORT_STATUS_INCORRECT
    ],
    [TRANSPORT_STATUS_SHIPPING_CREATED]: [
        TRANSPORT_STATUS_ON_THE_WAY,
        TRANSPORT_STATUS_CANCELED,
        TRANSPORT_STATUS_INCORRECT
    ],
    [TRANSPORT_STATUS_ON_THE_WAY]: [
        TRANSPORT_STATUS_AT_PICKUP_POINT,
        TRANSPORT_STATUS_DELIVERED,
        TRANSPORT_STATUS_RETURNING_BACK,
        TRANSPORT_STATUS_CANCELED,
        TRANSPORT_STATUS_INCORRECT,
        TRANSPORT_STATUS_LOST
    ],
    [TRANSPORT_STATUS_AT_PICKUP_POINT]: [
        TRANSPORT_STATUS_DELIVERED,
        TRANSPORT_STATUS_RETURNING_BACK,
        TRANSPORT_STATUS_CANCELED,
        TRANSPORT_STATUS_INCORRECT,
        TRANSPORT_STATUS_LOST
    ],
    [TRANSPORT_STATUS_DELIVERED]: [],
    [TRANSPORT_STATUS_RETURNING_BACK]: [
        TRANSPORT_STATUS_RETURNED_BACK,
        TRANSPORT_STATUS_CANCELED,
        TRANSPORT_STATUS_INCORRECT
    ],
    [TRANSPORT_STATUS_RETURNED_BACK]: [],
    [TRANSPORT_STATUS_CANCELED]: [],
    [TRANSPORT_STATUS_INCORRECT]: [
        TRANSPORT_STATUS_CREATED,
        TRANSPORT_STATUS_SHIPPING_CREATED,
        TRANSPORT_STATUS_CANCELED
    ],
    [TRANSPORT_STATUS_LOST]: []
};

export const TRANSPORT_STATUS_TIMESTAMP_FIELDS: Record<
    Exclude<TransportStatus, typeof TRANSPORT_STATUS_CREATED>,
    | 'SHIPPING_CREATED_AT'
    | 'ON_THE_WAY_AT'
    | 'AT_PICKUP_POINT_AT'
    | 'DELIVERED_AT'
    | 'RETURNING_BACK_AT'
    | 'RETURNED_BACK_AT'
    | 'CANCELED_AT'
    | 'INCORRECT_AT'
    | 'LOST_AT'
> = {
    [TRANSPORT_STATUS_SHIPPING_CREATED]: 'SHIPPING_CREATED_AT',
    [TRANSPORT_STATUS_ON_THE_WAY]: 'ON_THE_WAY_AT',
    [TRANSPORT_STATUS_AT_PICKUP_POINT]: 'AT_PICKUP_POINT_AT',
    [TRANSPORT_STATUS_DELIVERED]: 'DELIVERED_AT',
    [TRANSPORT_STATUS_RETURNING_BACK]: 'RETURNING_BACK_AT',
    [TRANSPORT_STATUS_RETURNED_BACK]: 'RETURNED_BACK_AT',
    [TRANSPORT_STATUS_CANCELED]: 'CANCELED_AT',
    [TRANSPORT_STATUS_INCORRECT]: 'INCORRECT_AT',
    [TRANSPORT_STATUS_LOST]: 'LOST_AT'
};

export const transportLogMessages = {
    createTransport: 'createTransport',
    updateTransport: 'updateTransport',
    updateTransportToStatus: 'updateTransportToStatus',
    updateTransportStatusSucces: 'updateTransportStatusSucces',
    updateTransportStatusFailure: 'updateTransportStatusFailure',
    errorWhileWrittingToDB: 'errorWhileWrittingToDB'
} as const;

export const MISSING_SHIPPER_MESSAGE = 'Missing shipper or shipper service code';
